import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useGlobal } from './GlobalContext';
import { getAvailableData } from '../features_utils';

const BuildContext = createContext();

export const useBuild = () => {
  const context = useContext(BuildContext);
  if (!context) {
    throw new Error('useBuild must be used within a BuildProvider');
  }
  return context;
};

export const BuildProvider = ({ children }) => {
  const { loginToken, apiKey, accountId, baseServiceUrl } = useGlobal();

  const [selectedIndex, setSelectedIndex] = useState(localStorage.getItem('selectedIndex') || 'farcaster-items');
  const [storiesEngine, setStoriesEngine] = useState(localStorage.getItem('storiesEngine') || '');
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchLastCall, setSearchLastCall] = useState(null);
  const [storiesResults, setStoriesResults] = useState(null);
  const [storiesLastCall, setStoriesLastCall] = useState(null);
  const [featuresResults, setFeaturesResults] = useState(null);
  const [featuresLastCall, setFeaturesLastCall] = useState(null);
  const [scoringResults, setScoringResults] = useState(null);
  const [scoringLastCall, setScoringLastCall] = useState(null);
  const [rankingResults, setRankingResults] = useState(null);
  const [rankingLastCall, setRankingLastCall] = useState(null);
  const [hydrationResults, setHydrationResults] = useState(null);
  const [hydrationLastCall, setHydrationLastCall] = useState(null);
  const [hits, setHits] = useState(null);
  const [listCache, setListCache] = useState({});

  const buildSteps = useMemo(() => {
    const hasCandidates = hits && hits.length > 0;
    const availableData = getAvailableData(hits);
    const { features, scores } = availableData;
    const hasFeaturesOrScores = (features.length > 0) || (scores.length > 0);
    return [
      { label: 'Search', enabled: true },
      { label: 'Stories', enabled: true },
      { label: 'Hydration', enabled: hasCandidates },
      { label: 'Features', enabled: hasCandidates },
      { label: 'Scoring', enabled: hasCandidates },
      { label: 'Ranking', enabled: hasCandidates && hasFeaturesOrScores },
      { label: 'DSL', enabled: true },
    ];
  }, [hits]);

  const clearResults = useCallback(() => {
    setSearchResults(null);
    setSearchLastCall(null);
    setFeaturesResults(null);
    setFeaturesLastCall(null);
    setScoringResults(null);
    setScoringLastCall(null);
    setRankingResults(null);
    setRankingLastCall(null);
    setHydrationResults(null);
    setHydrationLastCall(null);
    setStoriesResults(null);
    setStoriesLastCall(null);
    setHits(null);
  }, []);

  const clearListCache = useCallback(() => {
    setListCache({});
  }, []);

  useEffect(() => {
    clearListCache();
  }, [apiKey, clearListCache]);

  const updateSelectedIndex = useCallback((index) => {
    setSelectedIndex(index);
    localStorage.setItem('selectedIndex', index);
    clearResults();
    setSearchLastCall(null);
  }, [clearResults]);

  const updateStoriesEngine = useCallback((engine) => {
    setStoriesEngine(engine);
    localStorage.setItem('storiesEngine', engine);
  }, []);

  const apiCall = useCallback(async (endpoint, options = {}) => {
    if (!loginToken && !apiKey) {
      throw new Error('API key is required');
    }
    let authToken = 'Bearer ' ;
    if (loginToken) {
      //console.log('Using login token')
      authToken = authToken + loginToken;
      //console.log(authToken);
    } else {
      //console.log('Using API key');
      authToken = authToken + apiKey;
    }

    const url = `${baseServiceUrl}${endpoint}`;
    const method = options.method || 'GET';
    console.log('API call:', { url, method, endpoint, body: options.body || null });
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authToken,
        ...options.headers,
      },
    };
    if (apiKey) {
      defaultOptions.headers['x-api-key'] = apiKey;
    }
    if (accountId) {
      defaultOptions.headers['x-account'] = accountId;
    }

    const startTime = performance.now();
    const response = await fetch(url, { ...defaultOptions, ...options });
    const endTime = performance.now();
    const frontendTime = Math.round(endTime - startTime);

    if (!response.ok) {
      let errorBody = '';
      try {
        errorBody = await response.text();
      } catch (err) {
        errorBody = '';
      }
      console.error('API error:', { url, method, status: response.status, statusText: response.statusText, errorBody });
      throw new Error(`API call failed: ${response.status} ${response.statusText}${errorBody ? ` - ${errorBody}` : ''}`);
    }

    const result = await response.json();
    result.took_frontend = frontendTime;
    return result;
  }, [loginToken, apiKey, accountId, baseServiceUrl]);

  const fetchCachedList = useCallback(async (endpoint, service) => {
    const cacheKey = `${service}:${endpoint}`;
    if (listCache[cacheKey]) {
      return listCache[cacheKey];
    }
    try {
      const data = await apiCall(endpoint, {});
      setListCache(prev => ({ ...prev, [cacheKey]: data }));
      return data;
    } catch (error) {
      throw error;
    }
  }, [apiCall, listCache]);

  const searchApiIncludeVector = true;
  const searchIndex = useMemo(() => {
    return selectedIndex.replace(/-v\d{8}$/, '');
  }, [selectedIndex]);

  const buildSearchRequestBody = useCallback((queryParams, feedType, overrideIndex) => {
    return {
      index: overrideIndex || searchIndex,
      origin: 'frontend',
      feed_type: feedType,
      include_vector: searchApiIncludeVector,
      ...queryParams,
    };
  }, [searchIndex, searchApiIncludeVector]);

  const searchWithIndex = useCallback(async (endpoint, queryParams, feedType) => {
    const requestBody = buildSearchRequestBody(queryParams, feedType);
    setSearchLastCall({ endpoint, payload: requestBody });
    return await apiCall(endpoint, { method: 'POST', body: JSON.stringify(requestBody) });
  }, [apiCall, buildSearchRequestBody]);
  const frequentValuesIndex = useMemo(() => {
    return selectedIndex.replace(/-v\d{8}$/, '');
  }, [selectedIndex]);
  const searchApi = useMemo(() => ({
    generateFilterSortCandidates: async (queryParams) => {
      try {
        clearResults();
        setIsLoading(true);
        const result = await searchWithIndex('/search/filter_and_sort', queryParams, 'filter_and_sort');
        setSearchResults(result);
        setHits(result?.result?.hits);
        return result;
      } catch (err) {
        setSearchResults({ error: err.message || 'An unexpected error occurred' });
        throw err;
      } finally { setIsLoading(false); }
    },
    generateBoostCandidates: async (queryParams) => {
      try {
        clearResults();
        setIsLoading(true);
        const result = await searchWithIndex('/search/boost', queryParams, 'boost');
        setSearchResults(result);
        setHits(result?.result?.hits);
        return result;
      } catch (err) {
        setSearchResults({ error: err.message || 'An unexpected error occurred' });
        throw err;
      } finally { setIsLoading(false); }
    },
    generateSemanticSearchCandidates: async (queryParams) => {
      try {
        clearResults();
        setIsLoading(true);
        const result = await searchWithIndex('/search/semantic', queryParams, 'semantic');
        setSearchResults(result);
        setHits(result?.result?.hits);
        return result;
      } catch (err) {
        setSearchResults({ error: err.message || 'An unexpected error occurred' });
        throw err;
      } finally { setIsLoading(false); }
    },
    generateEsQueryCandidates: async (queryParams) => {
      try {
        clearResults();
        setIsLoading(true);
        const result = await searchWithIndex('/search/es_query', queryParams, 'es_query');
        setSearchResults(result);
        setHits(result?.result?.hits);
        return result;
      } catch (err) {
        setSearchResults({ error: err.message || 'An unexpected error occurred' });
        throw err;
      } finally { setIsLoading(false); }
    },
    generateAiPromptQuery: async (prompt) => {
      const result = await apiCall('/search/ai_prompt', { method: 'POST', body: JSON.stringify({ prompt: prompt.trim() }) });
      return result;
    },
    lookupDocument: async (index, docId) => {
      const result = await apiCall(`/search/document/${index}/${docId}`, { method: 'GET' });
      return result;
    },
    searchEphemeral: async (endpoint, payload) => {
      const result = await apiCall(endpoint, { method: 'POST', body: JSON.stringify(payload) });
      return result;
    },
  }), [apiCall, clearResults, searchWithIndex]);

  const storiesApi = useMemo(() => ({
    generateStories: async (engine, params) => {
      try {
        setStoriesResults(null);
        setIsLoading(true);
        const requestBody = { engine, params };
        const result = await apiCall('/stories/generate', { method: 'POST', body: JSON.stringify(requestBody) });
        setStoriesLastCall({ endpoint: '/stories/generate', payload: requestBody });
        setStoriesResults(result);
        return result;
      } catch (err) {
        setStoriesResults({ error: err.message || 'An unexpected error occurred' });
        throw err;
      } finally { setIsLoading(false); }
    },
  }), [apiCall]);

  const featuresApi = useMemo(() => ({
    getFeatures: async (version, payload) => {
      try {
        setFeaturesResults(null);
        setIsLoading(true);
        const requestBody = { origin: 'frontend', ...payload };
        const result = await apiCall(`/features/${version}`, { method: 'POST', body: JSON.stringify(requestBody) });
        setFeaturesLastCall({ endpoint: `/features/${version}`, payload: requestBody });
        setFeaturesResults(result);
        return result;
      } catch (err) {
        setFeaturesResults({ error: err.message || 'An unexpected error occurred' });
        throw err;
      } finally { setIsLoading(false); }
    },
  }), [apiCall]);

  const scoringApi = useMemo(() => ({
    getScores: async (endpoint, payload) => {
      try {
        setScoringResults(null);
        setIsLoading(true);
        const requestBody = { origin: 'frontend', ...payload };
        setScoringLastCall({ endpoint, payload: requestBody });
        const result = await apiCall(endpoint, { method: 'POST', body: JSON.stringify(requestBody) });
        setScoringResults(result);
        return result;
      } catch (err) {
        setScoringResults({ error: err.message || 'An unexpected error occurred' });
        throw err;
      } finally { setIsLoading(false); }
    },
  }), [apiCall]);

  const rankingApi = useMemo(() => ({
    rankFeed: async (payload) => {
      try {
        setRankingResults(null);
        setIsLoading(true);
        const requestBody = { origin: 'frontend', ...payload };
        setRankingLastCall({ endpoint: '/ranking/feed', payload: requestBody });
        const result = await apiCall('/ranking/feed', { method: 'POST', body: JSON.stringify(requestBody) });
        setRankingResults(result);
        return result;
      } catch (err) {
        setRankingResults({ error: err.message || 'An unexpected error occurred' });
        throw err;
      } finally { setIsLoading(false); }
    },
  }), [apiCall]);

  // Merge a hydration result onto the candidates.
  //   v3 hits-shape (preferred): server returns `result.hits` with each
  //     `_source` re-written to include `metadata.<target>` (and the legacy
  //     `<target>` dual-write). We replace each candidate's `_source` with
  //     the server's, and drop candidates the server filtered out.
  //   item_ids-shape (alternative): server returns `result.metadata` keyed
  //     by index/item_id; we walk it and merge per candidate.
  const addHydration = useCallback((hydrationResult) => {
    if (!hydrationResult || typeof hydrationResult !== 'object') return;
    const returnedHits = Array.isArray(hydrationResult.hits) ? hydrationResult.hits : null;
    setHits((prev) => {
      if (!Array.isArray(prev)) return prev;
      if (returnedHits) {
        const byId = {};
        for (const h of returnedHits) {
          if (h && typeof h === 'object' && h._id != null) byId[String(h._id)] = h;
        }
        const next = [];
        for (const hit of prev) {
          if (!hit || typeof hit !== 'object') continue;
          const id = hit._id != null ? String(hit._id) : null;
          const updated = id ? byId[id] : null;
          if (!updated) continue;   // server dropped via drop_empty_hits
          next.push({
            ...hit,
            _source: (updated._source && typeof updated._source === 'object')
              ? updated._source
              : hit._source,
          });
        }
        return next;
      }
      const metadata = hydrationResult.metadata;
      if (!metadata || typeof metadata !== 'object') return prev;
      const byId = {};
      for (const idx of Object.keys(metadata)) {
        const inner = metadata[idx] || {};
        for (const id of Object.keys(inner)) byId[String(id)] = inner[id] || {};
      }
      return prev.map((hit) => {
        if (!hit || typeof hit !== 'object') return hit;
        const id = hit._id != null ? String(hit._id) : null;
        const entry = id ? byId[id] : null;
        if (!entry) return hit;
        const src = hit._source && typeof hit._source === 'object' ? { ...hit._source } : {};
        const meta = src.metadata && typeof src.metadata === 'object' ? { ...src.metadata } : {};
        for (const target of Object.keys(entry)) {
          meta[target] = entry[target];
        }
        src.metadata = meta;
        return { ...hit, _source: src };
      });
    });
  }, []);

  const hydrationApi = useMemo(() => ({
    /** POST /hydration/run — body: {index, hits[] (preferred) OR item_ids[], drop_empty_hits?, hydrate?} */
    run: async (payload) => {
      try {
        setHydrationResults(null);
        setIsLoading(true);
        const requestBody = { origin: 'frontend', ...payload };
        setHydrationLastCall({ endpoint: '/hydration/run', payload: requestBody });
        const result = await apiCall('/hydration/run', { method: 'POST', body: JSON.stringify(requestBody) });
        setHydrationResults(result);
        if (result?.result) addHydration(result.result);
        return result;
      } catch (err) {
        setHydrationResults({ error: err.message || 'An unexpected error occurred' });
        throw err;
      } finally { setIsLoading(false); }
    },
    /** POST /hydration/describe — schema preview, no source lookup. */
    describe: async (payload) => {
      const requestBody = { origin: 'frontend', ...payload };
      return await apiCall('/hydration/describe', { method: 'POST', body: JSON.stringify(requestBody) });
    },
    /** POST /hydration/preview — one item_id, real lookup. */
    preview: async (payload) => {
      const requestBody = { origin: 'frontend', ...payload };
      return await apiCall('/hydration/preview', { method: 'POST', body: JSON.stringify(requestBody) });
    },
  }), [apiCall, addHydration]);

  const feedBuilderApi = useMemo(() => ({
    composeFeed: async (prompt, version, feed_type, feed_inputs) => {
      const result = await apiCall('/algo/compose', {
        method: 'POST',
        body: JSON.stringify({ prompt, version, feed_type, feed_inputs }),
      });
      return result;
    },
    validateFeed: async (algorithm, version, feed_type, feed_inputs) => {
      const result = await apiCall('/algo/validate', {
        method: 'POST',
        body: JSON.stringify({ algorithm, version, feed_type, feed_inputs }),
      });
      return result;
    },
    runFeed: async ({ sdk_version, feed_type, feed_inputs, farcasterFid, polymarketWallet, algorithm, signature, return_log }) => {
      const result = await apiCall('/run/algo', {
        method: 'POST',
        body: JSON.stringify({
          sdk_version, feed_type, feed_inputs,
          farcasterFid, polymarketWallet, algorithm, signature,
          return_log: Boolean(return_log),
        }),
      });
      return result;
    },
    callHelp: async (conversation, context = {}) => {
      const result = await apiCall('/algo/help', {
        method: 'POST',
        body: JSON.stringify({ conversation, context }),
      });
      return result;
    },
  }), [apiCall]);

  const value = {
    selectedIndex,
    storiesEngine,
    updateSelectedIndex,
    updateStoriesEngine,
    fetchCachedList,
    clearListCache,
    apiCall,
    isLoading,
    searchApi,
    searchResults,
    setSearchResults,
    searchLastCall,
    storiesApi,
    storiesResults,
    storiesLastCall,
    featuresApi,
    featuresResults,
    featuresLastCall,
    scoringApi,
    scoringResults,
    scoringLastCall,
    rankingApi,
    rankingResults,
    rankingLastCall,
    hydrationApi,
    hydrationResults,
    hydrationLastCall,
    addHydration,
    feedBuilderApi,
    buildSteps,
    hits,
    setHits,
    frequentValuesIndex,
  };

  return (
    <BuildContext.Provider value={value}>
      {children}
    </BuildContext.Provider>
  );
};
